import { DragEventHandler, memo, ReactNode, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useBlocker, useNavigate } from 'react-router'
import ReactFlow, {
    addEdge,
    Background,
    Connection,
    ControlButton,
    Controls,
    EdgeChange,
    EdgeTypes,
    MiniMap,
    Node,
    NodeChange,
    NodeMouseHandler,
    NodeTypes,
    OnEdgeUpdateFunc,
    ReactFlowInstance,
    updateEdge,
    useEdgesState,
    useNodesState,
} from 'reactflow'
import { JourneyContext, ProjectContext } from '../../contexts'
import { createUuid } from '../../utils'
import clsx from 'clsx'
import api from '../../api'
import { JourneyStep } from '../../types'

import './JourneyEditor.css'
import 'reactflow/dist/style.css'
import Modal from '../../ui/Modal'
import { toast } from 'react-hot-toast/headless'
import { JourneyForm } from './JourneyForm'
import { AutoLayoutIcon } from '../../ui/icons'
import { useTranslation } from 'react-i18next'
import { JourneyStepUsers } from './JourneyStepUsers'
import { JourneyStepNode } from './editor/JourneyStepNode'
import { JourneyStepEdge } from './editor/JourneyStepEdge'
import { JourneyDrawer } from './editor/JourneyDrawer'
import { JourneyStepEdit } from './editor/JourneyStepEdit'
import { DATA_FORMAT, getStepType, STEP_STYLE } from './editor/JourneyEditorUtils'
import { autoLayout } from './editor/JourneyAutoLayout'
import { useJourneyCopyPaste } from './editor/useJourneyCopyPaste'
import { useJourneySave } from './editor/useJourneySave'
import { useJourneyDeepLink } from './editor/useJourneyDeepLink'
import { JourneyEditorActions } from './editor/JourneyEditorActions'
import { JourneyStatsContext, JourneyStatsStatus, mergeStatsIntoNodes, nodesToSteps, stepsToNodes, wouldCreateCycle } from './editor/JourneyEditorHelpers'
import { RuleErrorsContext, ruleTemplateErrorMessage, useRuleErrors } from '../lists/rules/RuleHelpers'

const nodeTypes: NodeTypes = {
    step: memo(JourneyStepNode),
}

const edgeTypes: EdgeTypes = {
    step: memo(JourneyStepEdge),
}

export default function JourneyEditor() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [flowInstance, setFlowInstance] = useState<null | ReactFlowInstance>(null)
    const wrapper = useRef<HTMLDivElement>(null)

    const [project] = useContext(ProjectContext)
    const [journey, setJourney] = useContext(JourneyContext)

    const [nodes, setNodes, onNodesChange] = useNodesState([])
    const [edges, setEdges, onEdgesChange] = useEdgesState([])

    const journeyId = journey.id
    const isDeleted = !!journey.deleted_at
    const isDraft = journey.status === 'draft' && !isDeleted
    const draftId = journey.draft_id
    const parentId = journey.parent_id

    const [statsStatus, setStatsStatus] = useState<JourneyStatsStatus>('loading')

    useEffect(() => {
        let cancelled = false
        setStatsStatus('loading')

        // Fire both requests in parallel — but render steps as soon as they
        // arrive (don't block on stats), and apply stats *after* nodes are
        // set so the merge always runs against populated state, never [].
        const statsPromise = api.journeys.steps.stats(project.id, journeyId)
            .catch(() => null)

        void (async () => {
            const steps = await api.journeys.steps.get(project.id, journeyId)
            if (cancelled) return

            const { edges, nodes } = stepsToNodes(steps, {
                setViewUsersStep,
            })
            setNodes(nodes)
            setEdges(edges)

            const stats = await statsPromise
            if (cancelled) return
            if (!stats) {
                setStatsStatus('error')
                return
            }
            setNodes(prev => mergeStatsIntoNodes(prev, stats))
            setStatsStatus('ready')
        })()

        return () => { cancelled = true }
    }, [journeyId])

    // Deep-link to a specific node via ?step=<stepId> or ?node=<nodeId>
    useJourneyDeepLink({ flowInstance, nodes, setNodes })

    const [saving, setSaving] = useState(false)
    const [publishing, setPublishing] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [viewUsersStep, setViewUsersStep] = useState<null | { stepId: number, stepType: string }>(null)

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    )

    useEffect(() => {
        if (blocker.state !== 'blocked') return
        if (confirm(t('confirm_unsaved_changes'))) {
            blocker.proceed()
        } else {
            blocker.reset()
        }
    }, [blocker.state])

    const handleSetNodes = (nodes: SetStateAction<Array<Node<any, string | undefined>>>) => {
        setHasUnsavedChanges(true)
        setNodes(nodes)
    }

    const handleNodesChange = useCallback((changes: NodeChange[]) => {
        if (changes.some(c => c.type === 'remove' || (c.type === 'position' && c.dragging))) {
            setHasUnsavedChanges(true)
        }
        onNodesChange(changes)
    }, [onNodesChange])

    const handleEdgesChange = useCallback((changes: EdgeChange[]) => {
        if (changes.some(c => c.type === 'remove')) {
            setHasUnsavedChanges(true)
        }
        onEdgesChange(changes)
    }, [onEdgesChange])

    const createDraft = async () => {
        setSaving(true)
        try {
            const newDraft = await api.journeys.version(project.id, journey.id)
            setJourney(newDraft)
            editDraft(newDraft.id)
        } finally {
            setSaving(false)
        }
    }

    const editDraft = (id: number) => {
        window.location.href = `/projects/${project.id}/journeys/${id}`
    }

    const closeDraft = () => {
        window.location.href = `/projects/${project.id}/journeys/${parentId}`
    }

    const restoreJourney = async () => {
        if (!confirm(t('journey_restore_confirm'))) return
        await api.journeys.restore(project.id, journey.id)
        window.location.reload()
    }

    const deleteDraft = async () => {
        if (!confirm(t('journey_draft_delete_confirm'))) return
        await api.journeys.delete(project.id, journey.id)
        toast.success(t('journey_draft_deleted'))
        if (parentId) {
            window.location.href = `/projects/${project.id}/journeys/${parentId}`
        } else {
            await navigate('../journeys')
        }
    }

    const saveDraft = async () => {
        // Stats are fetched separately and stripped from the save response, so
        // capture them from current nodes and re-apply after the rebuild —
        // otherwise saving wipes them out until the next 15s stats refresh.
        const priorStats: Record<number, any> = {}
        for (const node of nodes) {
            if (node.data?.stepId && node.data?.stats) {
                priorStats[node.data.stepId] = node.data.stats
            }
        }

        const stepMap = await api.journeys.steps.set(project.id, journey.id, nodesToSteps(nodes, edges))
        const refreshed = stepsToNodes(stepMap, { setViewUsersStep })

        setNodes(mergeStatsIntoNodes(refreshed.nodes, priorStats))
        setEdges(refreshed.edges)
    }

    const saveErrorMessage = (error: any): string => {
        const serverError = error?.response?.data?.error ?? error?.message ?? String(error)
        // The cycle rejection deserves guidance, not a raw server message
        if (error?.response?.status === 422 && `${serverError}`.includes('cycle')) {
            return t('journey_cycle_error')
        }
        return t('unable_to_save', { error: serverError })
    }

    const ruleErrors = useRuleErrors()
    const showSaveError = (error: any) => {
        const details = ruleErrors.setFromResponse(error)
        if (!details) {
            toast.error(saveErrorMessage(error))
            return
        }
        toast.error(t('unable_to_save', { error: ruleTemplateErrorMessage(t, details) }))
        if (details.step_id) {
            setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, editing: n.id === details.step_id } })))
        }
    }

    const saveSteps = useCallback(async () => {

        setSaving(true)
        try {
            await saveDraft()

            // Only clear on success — a failed save must keep the navigation
            // blocker armed or the user can walk away and lose their changes
            setHasUnsavedChanges(false)
            toast.success(t('journey_saved'))
        } catch (error: any) {
            showSaveError(error)
        } finally {
            setSaving(false)
        }
    }, [project, journey, nodes, edges])

    const publishJourney = async () => {
        if (!confirm(t('journey_publish_confirmation'))) return

        setPublishing(true)
        try {
            if (hasUnsavedChanges) {
                await saveDraft()
                setHasUnsavedChanges(false)
            }
            await api.journeys.publish(project.id, journey.id)
            window.location.href = `/projects/${project.id}/journeys/${journey.parent_id ?? journey.id}`
            toast.success(t('journey_published'))
        } catch (error: any) {
            showSaveError(error)
        } finally {
            setPublishing(false)
        }
    }

    const onConnect = useCallback(async (connection: Connection) => {
        const sourceNode = nodes.find(n => n.id === connection.source)
        const data = await getStepType(sourceNode?.data.type)?.newEdgeData?.() ?? {}
        setHasUnsavedChanges(true)
        setEdges(edges => addEdge({
            ...connection,
            type: STEP_STYLE,
            data,
        }, edges))
    }, [nodes, setEdges])

    const onEdgeUpdate = useCallback<OnEdgeUpdateFunc>((prev, next) => {
        // Reconnecting an existing edge must not create a loop; exclude the
        // edge being moved since its old position no longer counts
        const otherEdges = edges.filter(e => e.id !== prev.id)
        if (wouldCreateCycle(otherEdges, next.source, next.target)) return
        setHasUnsavedChanges(true)
        setEdges(edges => updateEdge(prev, next, edges))
    }, [edges, setEdges])

    // Flow-level guard: unlike the per-handle validator, this also covers edge
    // reconnection and connections dragged from a step's top (target) handle
    const isValidConnection = useCallback((conn: Connection) =>
        !wouldCreateCycle(edges, conn.source, conn.target), [edges])

    const onDragOver = useCallback<DragEventHandler>(event => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
    }, [])

    const onDrop = useCallback<DragEventHandler>(async event => {

        event.preventDefault()
        if (!wrapper.current || !flowInstance) return

        const bounds = wrapper.current.getBoundingClientRect()
        const payload: {
            type: string
            x: number
            y: number
        } = JSON.parse(event.dataTransfer.getData(DATA_FORMAT))
        const type = getStepType(payload.type)

        if (!type) return

        const { x, y } = flowInstance.project({
            x: event.clientX - bounds.left - (payload.x ?? 0),
            y: event.clientY - bounds.top - (payload.y ?? 0),
        })

        const newStep = {
            id: createUuid(),
            position: {
                x,
                y,
            },
            type: 'step',
            data: {
                type: payload.type,
                data: await type.newData?.() ?? {},
            },
        }

        handleSetNodes(nds => nds.concat(newStep))

    }, [setNodes, flowInstance, project, journey])

    const onLayout = useCallback(() => {
        if (!flowInstance) return

        // Get nodes with current dimensions from ReactFlow instance
        const currentNodes = flowInstance.getNodes()

        const layoutedNodes = autoLayout(currentNodes, edges, {
            defaultNodeWidth: 200,
            defaultNodeHeight: 200,
            horizontalSpacing: 100,
            verticalSpacing: 100,
        })

        // Batch the update
        handleSetNodes(layoutedNodes)
    }, [flowInstance, edges, handleSetNodes])

    // Set up copy/paste keyboard shortcuts
    useJourneyCopyPaste({
        nodes,
        edges,
        isDraft,
        flowInstance,
        handleSetNodes,
        setEdges,
    })

    // Set up Cmd/Ctrl+S to save
    useJourneySave({
        isDraft,
        saving,
        saveSteps,
    })

    const [editOpen, setEditOpen] = useState(false)
    const editNode = nodes.find(n => n.data.editing)

    const onNodeDoubleClick = useCallback<NodeMouseHandler>((_, n) => {
        setNodes(nds => nds.map(x => x.id === n.id
            ? {
                ...n,
                data: {
                    ...n.data,
                    editing: true,
                },
            }
            : x,
        ))
        const x = n.position.x + ((n.width ?? 120) / 2)
        const y = n.position.y + ((n.height ?? 120) / 2)
        setTimeout(() => flowInstance?.setCenter(x, y, { zoom: 1 }), 10)
    }, [flowInstance?.setCenter])

    let stepEdit: ReactNode = null
    if (editNode) {
        const type = getStepType(editNode.data.type)
        if (type) {
            stepEdit = <RuleErrorsContext.Provider value={ruleErrors.value}>
                <JourneyStepEdit
                    editNode={editNode}
                    type={type}
                    nodes={nodes}
                    setViewUsersStep={setViewUsersStep}
                    handleSetNodes={handleSetNodes}
                    disabled={!isDraft}
                />
            </RuleErrorsContext.Provider>
        }
    }

    return (
        <Modal
            size="fullscreen"
            title={journey.name}
            open={true}
            onClose={async () => { await navigate('../journeys') }}
            actions={
                <JourneyEditorActions
                    journey={journey}
                    project={project}
                    isDeleted={isDeleted}
                    isDraft={isDraft}
                    parentId={parentId}
                    draftId={draftId}
                    saving={saving}
                    publishing={publishing}
                    setEditOpen={setEditOpen}
                    saveSteps={saveSteps}
                    publishJourney={publishJourney}
                    restoreJourney={restoreJourney}
                    createDraft={createDraft}
                    editDraft={editDraft}
                    closeDraft={closeDraft}
                    deleteDraft={deleteDraft}
                    hasUnsavedChanges={hasUnsavedChanges}
                />
            }
        >
            <div className={clsx('journey', editNode && 'editing')}>
                <div className="journey-builder" ref={wrapper}>
                    <JourneyStatsContext.Provider value={statsStatus}>
                        <ReactFlow
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={handleNodesChange}
                            onEdgesChange={handleEdgesChange}
                            onConnect={onConnect}
                            onEdgeUpdate={onEdgeUpdate}
                            isValidConnection={isValidConnection}
                            onInit={setFlowInstance}
                            onNodeDoubleClick={onNodeDoubleClick}
                            onClick={() => {
                                if (editNode) {
                                    setNodes(nds => nds.map(n => n.data.editing ? { ...n, data: { ...n.data, editing: false } } : n))
                                }
                            }}
                            nodesDraggable={isDraft}
                            nodesConnectable={isDraft}
                            deleteKeyCode={['Backspace', 'Delete']}
                            onDragOver={onDragOver}
                            onDrop={onDrop}
                            panOnScroll
                            selectNodesOnDrag
                            fitView
                            maxZoom={1}
                            minZoom={0.1}
                            zoomOnDoubleClick={false}
                        >
                            <Background className="internal-canvas" />
                            {
                                !editNode && (
                                    <>
                                        <Controls showInteractive={isDraft}>
                                            {isDraft && (
                                                <ControlButton
                                                    onClick={onLayout}
                                                    title={t('auto_layout')}
                                                >
                                                    <AutoLayoutIcon />
                                                </ControlButton>
                                            )}
                                        </Controls>
                                        <MiniMap
                                            nodeClassName={({ data }: Node<JourneyStep>) => `journey-minimap ${getStepType(data.type)?.category ?? 'unknown'}`}
                                        />
                                    </>
                                )
                            }
                        </ReactFlow>
                    </JourneyStatsContext.Provider>
                </div>

                <JourneyDrawer isDraft={isDraft}>
                    {stepEdit}
                </JourneyDrawer>
            </div>
            <Modal
                open={editOpen}
                onClose={setEditOpen}
                title={t('edit_journey_details')}
            >
                <JourneyForm
                    journey={journey}
                    onSaved={async journey => {
                        setEditOpen(false)
                        setJourney(journey)
                    }}
                />
            </Modal>
            {!!viewUsersStep && <JourneyStepUsers
                open={!!viewUsersStep}
                onClose={() => setViewUsersStep(null)}
                stepType={viewUsersStep.stepType}
                stepId={viewUsersStep.stepId}
            />}
        </Modal>
    )
}
